import { useEffect, useRef, useState } from 'react'

/**
 * useReveal menampilkan elemen ketika ia masuk ke layar.
 *
 * Memakai IntersectionObserver, bukan penyimak peristiwa scroll: pengamat
 * bawaan peramban tidak berjalan pada setiap piksel gulir, sehingga halaman
 * panjang tetap ringan. Pengamatan dilepas setelah elemen muncul — animasi
 * masuk hanya perlu terjadi sekali.
 */
const revealedClass = 'revealed'

/**
 * Elemen yang masih menunggu giliran muncul, beserta satu pengamat bersama
 * untuk semuanya. Satu pengamat untuk banyak elemen jauh lebih murah daripada
 * satu pengamat per elemen, dan membuat penyapuan di bawah ini mungkin
 * dilakukan.
 */
const pending = new Set<Element>()
let observer: IntersectionObserver | null = null
let sweepQueued = false

function reveal(element: Element) {
  element.classList.add(revealedClass)
  pending.delete(element)
  observer?.unobserve(element)
}

/**
 * Menampilkan semua elemen yang kini berada di atas garis bawah layar.
 *
 * Lompatan gulir — tautan jangkar, tombol kembali ke atas, Cmd+↓, atau
 * pemulihan posisi gulir saat halaman dibuka ulang — dapat melewati sebuah
 * elemen tanpa satu frame pun beririsan dengan layar. Pengamat tidak pernah
 * dipanggil untuk elemen semacam itu, dan tanpa penyapuan ini bagian tersebut
 * akan tinggal kosong selamanya.
 */
function sweep() {
  sweepQueued = false
  const limit = window.innerHeight

  for (const element of Array.from(pending)) {
    if (element.getBoundingClientRect().top < limit) reveal(element)
  }
}

function queueSweep() {
  if (sweepQueued) return
  sweepQueued = true
  requestAnimationFrame(sweep)
}

function sharedObserver() {
  if (observer) return observer

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) reveal(entry.target)
      }
    },
    // Ambang kecil dengan margin bawah negatif: elemen mulai muncul sesaat
    // sebelum benar-benar terlihat penuh, sehingga terasa mengalir alih-alih
    // tersentak.
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
  )

  window.addEventListener('scroll', queueSweep, { passive: true })
  window.addEventListener('resize', queueSweep, { passive: true })

  return observer
}

/**
 * useReveal menampilkan elemen ketika ia masuk ke layar.
 *
 * Memakai IntersectionObserver, bukan penyimak peristiwa gulir: pengamat
 * bawaan peramban tidak berjalan pada setiap piksel gulir, sehingga halaman
 * panjang tetap ringan. Pengamatan dilepas setelah elemen muncul — animasi
 * masuk hanya perlu terjadi sekali.
 */
export function useReveal<T extends HTMLElement>(delayMs = 0) {
  const ref = useRef<T | null>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    // Pengguna yang meminta gerak seminimal mungkin langsung melihat isinya,
    // tanpa menunggu pengamat apa pun.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      element.classList.add(revealedClass)
      return
    }

    element.style.setProperty('--reveal-delay', `${delayMs}ms`)

    pending.add(element)
    sharedObserver().observe(element)

    return () => {
      pending.delete(element)
      observer?.unobserve(element)
    }
  }, [delayMs])

  return ref
}

/**
 * useCountUp menaikkan angka dari nol ke nilai akhirnya.
 *
 * Dipakai pada kartu statistik halaman awal. Nilai akhir selalu ditulis apa
 * adanya di DOM sejak awal bagi pembaca layar; yang beranimasi hanya teks
 * yang terlihat.
 */
export function useCountUp(target: number, durationMs = 1200) {
  const ref = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let frame = 0
    let start: number | null = null

    const step = (timestamp: number) => {
      if (start === null) start = timestamp
      const progress = Math.min(1, (timestamp - start) / durationMs)
      // Pelambatan di ujung membuat angka seperti mendarat, bukan berhenti
      // mendadak.
      const eased = 1 - Math.pow(1 - progress, 3)
      element.textContent = Math.round(target * eased).toLocaleString('id-ID')

      if (progress < 1) frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [target, durationMs])

  return ref
}

/* ---------------------------------------------------------------------------
   Penggerak berbasis gulir
--------------------------------------------------------------------------- */

/**
 * useScrollProgress mengembalikan ref untuk sebuah wadah dan menuliskan
 * kemajuan gulir 0..1 ke dalamnya sebagai variabel CSS `--p`.
 *
 * Hanya satu variabel yang ditulis per frame, pada satu elemen. Seluruh anak
 * di dalamnya menghitung transformnya sendiri lewat calc(), sehingga jumlah
 * penulisan gaya tidak bertambah seiring banyaknya kartu.
 *
 * Nilai berjalan dua arah: saat halaman baru dibuka ia beranjak dari 1 ke 0
 * sebagai animasi masuk, lalu setelah itu gulir yang menentukannya. Keduanya
 * memakai satu variabel yang sama supaya tidak pernah ada dua sumber gerak
 * yang saling menimpa.
 */
export function useScrollProgress<T extends HTMLElement>(distance = 280, entranceMs = 900) {
  const ref = useRef<T | null>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      element.style.setProperty('--p', '0')
      return
    }

    let entranceDone = false
    let frame = 0
    let start: number | null = null

    const write = (value: number) => element.style.setProperty('--p', value.toFixed(4))

    const fromScroll = () => Math.min(1, Math.max(0, window.scrollY / distance))

    const entrance = (timestamp: number) => {
      if (start === null) start = timestamp
      const t = Math.min(1, (timestamp - start) / entranceMs)
      // Pelambatan kuat di ujung: kartu melesat keluar lalu mendarat pelan.
      const eased = 1 - Math.pow(1 - t, 4)
      write(1 - eased)

      if (t < 1) {
        frame = requestAnimationFrame(entrance)
        return
      }
      entranceDone = true
      write(fromScroll())
    }

    // Halaman yang dibuka dalam keadaan sudah tergulir — misalnya karena
    // posisi gulir dipulihkan — tidak perlu animasi masuk sama sekali.
    if (window.scrollY > 4) {
      entranceDone = true
      write(fromScroll())
    } else {
      write(1)
      frame = requestAnimationFrame(entrance)
    }

    let queued = false
    const onScroll = () => {
      if (!entranceDone || queued) return
      queued = true
      requestAnimationFrame(() => {
        queued = false
        write(fromScroll())
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
    }
  }, [distance, entranceMs])

  return ref
}

/**
 * useHeaderHide menyembunyikan bilah navigasi saat pengguna menggulir turun
 * dan memunculkannya kembali begitu menggulir naik.
 *
 * Ambang kecil mencegah bilah berkedip-kedip pada gulir halus milik trackpad,
 * dan bilah selalu tampil kembali di dekat puncak halaman.
 */
export function useHeaderHide(threshold = 90) {
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let last = window.scrollY
    let queued = false

    const evaluate = () => {
      queued = false
      const current = window.scrollY
      const delta = current - last

      if (Math.abs(delta) < 6) return
      last = current

      if (current < threshold) {
        setHidden(false)
        return
      }
      setHidden(delta > 0)
    }

    const onScroll = () => {
      if (queued) return
      queued = true
      requestAnimationFrame(evaluate)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [threshold])

  return hidden
}
